import { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip as Tag,
} from '@material-ui/core';

import { RiArrowRightSLine } from '@remixicon/react';

import { SimpleAutocomplete } from '../SimpleAutocomplete';
import { useGroupedAutocompleteContext } from './context';
import { useStyles } from './styles';
import type { GroupedAutocompleteItem } from './types';

type GroupedAutocompleteGroupsProps = {
  items: GroupedAutocompleteItem[];
  loading?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  // When provided, filtering is delegated to the caller (e.g. a server-side
  // search) instead of matching client-side against `items`.
  onSearchChange?: (query: string) => void;
  onLoadMore?: () => void;
};

export const GroupedAutocompleteGroups = ({
  items,
  loading,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No groups available',
  onSearchChange,
  onLoadMore,
}: GroupedAutocompleteGroupsProps) => {
  const { activeGroup, onActiveGroupChange, selectedItems, groupItemsRef } =
    useGroupedAutocompleteContext();
  const classes = useStyles();

  groupItemsRef.current = items;

  const selectedCountByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [group, values] of Object.entries(selectedItems)) {
      if (values.length > 0) counts[group] = values.length;
    }
    return counts;
  }, [selectedItems]);

  const sortedGroups = useMemo(() => {
    const selected = items.filter(g => selectedCountByGroup[g.value]);
    const rest = items.filter(g => !selectedCountByGroup[g.value]);
    return [...selected, ...rest];
  }, [items, selectedCountByGroup]);

  if (activeGroup !== null) return null;

  return (
    <SimpleAutocomplete.Root
      items={sortedGroups}
      disableFilter={Boolean(onSearchChange)}
      onInputChange={onSearchChange}
      onSelect={groupId => {
        onActiveGroupChange(groupId);
        return true;
      }}
    >
      {({ items: filteredGroups, getListboxProps, getOptionProps }) => (
        <Box className={classes.root}>
          <SimpleAutocomplete.Input
            autoFocus
            placeholder={searchPlaceholder}
            margin="dense"
          />
          <Box className={classes.list}>
            {loading ? (
              <Box className={classes.centered}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <>
                <SimpleAutocomplete.Empty>
                  {items.length === 0 ? emptyMessage : 'No matches'}
                </SimpleAutocomplete.Empty>
                {filteredGroups.length > 0 && (
                  <List {...getListboxProps()}>
                    {filteredGroups.map((group, i) => {
                      const count = selectedCountByGroup[group.value] ?? 0;
                      return (
                        <ListItem
                          key={group.value}
                          button
                          dense
                          className={classes.groupRow}
                          {...getOptionProps(i)}
                        >
                          <ListItemText
                            primary={group.label || group.value}
                            primaryTypographyProps={{
                              variant: 'body2',
                              noWrap: true,
                              title: group.label || group.value,
                            }}
                          />
                          {count > 0 && (
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              style={{
                                gap: 'var(--bui-space-2)',
                              }}
                              aria-label="Selected count"
                              className={classes.selectedCount}
                            >
                              <Tag
                                id={`count-${group.value}`}
                                size="small"
                                label={count}
                              />
                            </Box>
                          )}
                          <RiArrowRightSLine
                            size={18}
                            className={classes.groupChevron}
                          />
                        </ListItem>
                      );
                    })}
                    {onLoadMore && (
                      <InView
                        onChange={inView => {
                          if (inView) onLoadMore();
                        }}
                      />
                    )}
                  </List>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </SimpleAutocomplete.Root>
  );
};
